import { extendTheme } from '@mui/joy';

import siflyTheme from './sifly';

/**
 * Crew-specific theme extending the base sifly theme
 * Uses Open Sans font and includes Crew-specific colors
 */
const crewThemeTokens = {
  fontFamily: {
    display: '"Open Sans", sans-serif', // Font for headings and display text
    body: '"Open Sans", sans-serif' // Global default font for Crew view
  },
  components: {
    JoyTypography: {
      styleOverrides: {
        root: ({ ownerState }: { ownerState: { level?: string } }) => ({
          fontFamily: '"Open Sans", sans-serif',
          ...(ownerState.level === 'h3' && {
            fontWeight: 300,
            lineHeight: '100%'
          }),
          ...(ownerState.level === 'h2' && {
            fontWeight: 700,
            fontSize: '1.75rem',
            letterSpacing: '-0.02em'
          }),
          ...(ownerState.level === 'body-xs' && {
            fontSize: '0.625rem',
            lineHeight: '100%',
            fontWeight: 400
          }),
          ...(ownerState.level === 'body-md' && {
            fontSize: '0.875rem',
            lineHeight: '150%',
            fontWeight: 400
          }),
          ...(ownerState.level === 'body-lg' && {
            fontSize: '1rem',
            lineHeight: 1.6,
            fontWeight: 400
          }),
          ...(ownerState.level === 'title-sm' && {
            fontSize: '0.875rem',
            lineHeight: '142%',
            fontWeight: 400
          }),
          ...(ownerState.level === 'title-lg' && {
            fontSize: '1.125rem',
            lineHeight: 1.66,
            fontWeight: 500,
            letterSpacing: 0
          }),
          ...(ownerState.level === 'body-sm' && {
            fontSize: '14px',
            lineHeight: 1.5,
            fontWeight: 400
          })
        })
      }
    }
  },
  colorSchemes: {
    light: {
      palette: {
        ...siflyTheme.colorSchemes.light.palette,
        // Ensure background.surface is set for light mode (from Figma: #fbfcfe)
        background: {
          ...siflyTheme.colorSchemes.light.palette.background,
          surface: '#fbfcfe', // From Figma variable background/surface
          errorPage: '#F0EFF3' // Error page background from Figma
        },
        // Crew-specific indicator colors
        crew: {
          // Battery indicator colors
          batteryGreen: '#3AAD00',
          batteryYellow: '#F5D907',
          batteryRed: '#ED1C24',
          batteryGray: '#808080',
          // Thrust indicator colors
          thrustBlue: '#3F7EC9',
          thrustGreen: '#3AAD00',
          thrustYellow: '#F5D907',
          thrustOrange: '#FF9300',
          thrustRed: '#ED1C24',
          // Compass colors
          compassGreen: '#3AAD00',
          compassWindRed: '#E8181B',
          // Attitude indicator colors
          attitudeSkyBlue: '#4480CC',
          attitudeGroundGreen: '#3AAD00',
          // Accent colors
          accentYellow: '#FFCA2A',
          accentYellowAlt: '#FFC82A',
          // Icon colors
          iconGray: '#636B74',
          iconLightGray: '#F0F4F8'
        }
      }
    },
    dark: {
      palette: {
        ...siflyTheme.colorSchemes.dark.palette,
        // Ensure background.surface is set for dark mode (from Figma: #0b0d0e)
        background: {
          ...siflyTheme.colorSchemes.dark.palette.background,
          surface: '#0B0D0E', // Dark surface color from Figma (background/surface)
          errorPage: '#1D1E1C' // Error page background for dark mode (very dark gray from Figma)
        },
        // Crew-specific indicator colors (same as light for consistency)
        crew: {
          // Battery indicator colors
          batteryGreen: '#3AAD00',
          batteryYellow: '#F5D907',
          batteryRed: '#ED1C24',
          batteryGray: '#808080',
          // Thrust indicator colors
          thrustBlue: '#3F7EC9',
          thrustGreen: '#3AAD00',
          thrustYellow: '#F5D907',
          thrustOrange: '#FF9300',
          thrustRed: '#ED1C24',
          // Compass colors
          compassGreen: '#3AAD00',
          compassWindRed: '#E8181B',
          // Attitude indicator colors
          attitudeSkyBlue: '#4480CC',
          attitudeGroundGreen: '#63AA00',
          // Accent colors
          accentYellow: '#FFCA2A',
          accentYellowAlt: '#FFC82A',
          // Icon colors
          iconGray: '#636B74',
          iconLightGray: '#F0F4F8'
        }
      }
    }
  }
};

// Merge sifly theme with crew-specific overrides
// extendTheme merges the tokens deeply, so we merge manually first
const mergedTheme = {
  ...siflyTheme,
  ...crewThemeTokens,
  colorSchemes: {
    light: {
      ...siflyTheme.colorSchemes.light,
      palette: {
        ...siflyTheme.colorSchemes.light.palette,
        ...crewThemeTokens.colorSchemes.light.palette
      }
    },
    dark: {
      ...siflyTheme.colorSchemes.dark,
      palette: {
        ...siflyTheme.colorSchemes.dark.palette,
        ...crewThemeTokens.colorSchemes.dark.palette
      }
    }
  }
};

export default extendTheme(mergedTheme);
